import apiClient from './api';
import { CrewMember, NextOfKin, CsvImportResult, QualificationMatrix } from '../types';

/**
 * Service module for crew member API operations.
 * Requirements: 1.1, 1.5, 1.6, 2.1, 2.4
 */

/**
 * Data for creating a new crew member.
 * Requirements: 1.1, 1.3
 */
export interface CreateCrewMemberData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;  // ISO format YYYY-MM-DD (required)
  asNumber: string;
  nextOfKin?: NextOfKin;
  plbHexNumber?: string;
  phoneNumber?: string;
}

/**
 * Data for updating an existing crew member.
 * Requirements: 1.5
 */
export interface UpdateCrewMemberData {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  asNumber?: string;
  nextOfKin?: NextOfKin;
  plbHexNumber?: string;
  phoneNumber?: string;
}

/**
 * Data for adding a certificate to a crew member.
 * Requirements: 2.1
 */
export interface AddCertificateData {
  type: string;
  name: string;
  expiryDate?: string;
  documentId?: string;
}

// Helper to convert camelCase to snake_case for API requests
const toSnakeCase = (data: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[snakeKey] = toSnakeCase(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[snakeKey] = value.map(item => 
        typeof item === 'object' && item !== null ? toSnakeCase(item as Record<string, unknown>) : item
      );
    } else {
      result[snakeKey] = value;
    }
  }
  return result;
};

// Helper to convert snake_case API response to camelCase
const toCamelCase = (data: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[camelKey] = toCamelCase(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[camelKey] = value.map(item => 
        typeof item === 'object' && item !== null ? toCamelCase(item as Record<string, unknown>) : item
      );
    } else {
      result[camelKey] = value;
    }
  }
  return result;
};

export const crewService = {
  /** Fetch all crew members for the authenticated user. */
  getCrew: async (): Promise<CrewMember[]> => {
    const response = await apiClient.get<{ crew_members?: unknown[]; data?: unknown[] }>('/crew');
    const rawData = response.data.crew_members || response.data.data || response.data;
    if (Array.isArray(rawData)) {
      return rawData.map(item => toCamelCase(item as Record<string, unknown>) as unknown as CrewMember);
    }
    return [];
  },

  /**
   * Add a single crew member with comprehensive details.
   * Requirements: 1.1, 1.3
   */
  addCrewMember: async (data: CreateCrewMemberData): Promise<CrewMember> => {
    const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
    const response = await apiClient.post<{ crew_member?: unknown; data?: unknown }>('/crew', snakeData);
    const rawData = response.data.crew_member || response.data.data || response.data;
    return toCamelCase(rawData as Record<string, unknown>) as unknown as CrewMember;
  },

  /**
   * Update an existing crew member.
   * Requirements: 1.5
   */
  updateCrewMember: async (crewId: string, data: UpdateCrewMemberData): Promise<CrewMember> => {
    const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
    const response = await apiClient.put<{ crew_member?: unknown; data?: unknown }>(
      `/crew/${encodeURIComponent(crewId)}`,
      snakeData
    );
    const rawData = response.data.crew_member || response.data.data || response.data;
    return toCamelCase(rawData as Record<string, unknown>) as unknown as CrewMember;
  },

  /**
   * Delete a crew member and remove all event associations.
   * Requirements: 1.6
   */
  deleteCrewMember: async (crewId: string): Promise<void> => {
    await apiClient.delete(`/crew/${encodeURIComponent(crewId)}`);
  },

  /**
   * Add a certificate to a crew member.
   * Requirements: 2.1
   */
  addCertificate: async (crewId: string, certificate: AddCertificateData): Promise<CrewMember> => {
    const snakeData = toSnakeCase(certificate as unknown as Record<string, unknown>);
    const response = await apiClient.post<{ crew_member?: unknown; data?: unknown }>(
      `/crew/${encodeURIComponent(crewId)}/certificates`,
      snakeData
    );
    const rawData = response.data.crew_member || response.data.data || response.data;
    return toCamelCase(rawData as Record<string, unknown>) as unknown as CrewMember;
  },

  /**
   * Remove a certificate from a crew member.
   * Requirements: 2.4
   */
  removeCertificate: async (crewId: string, certificateId: string): Promise<CrewMember> => {
    const response = await apiClient.delete<{ crew_member?: unknown; data?: unknown }>(
      `/crew/${encodeURIComponent(crewId)}/certificates/${encodeURIComponent(certificateId)}`
    );
    const rawData = response.data.crew_member || response.data.data || response.data;
    return toCamelCase(rawData as Record<string, unknown>) as unknown as CrewMember;
  },

  /** Bulk import crew members via CSV file upload. */
  bulkImport: async (file: File): Promise<CsvImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<CsvImportResult>('/crew/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /** Fetch the crew qualification matrix for a specific race event. */
  getQualificationMatrix: async (raceId: string): Promise<QualificationMatrix> => {
    const response = await apiClient.get<QualificationMatrix>(
      `/races/${raceId}/qualification-matrix`
    );
    return response.data;
  },

  /**
   * Trigger on-demand AS number verification for a crew member.
   * Requirements: 7.2
   */
  verifyAsNumber: async (crewId: string): Promise<void> => {
    await apiClient.post(`/crew/${encodeURIComponent(crewId)}/verify-as`);
  },

  /**
   * Fetch a single crew member by ID (used for polling verification status).
   * Requirements: 7.1
   */
  getCrewMember: async (crewId: string): Promise<CrewMember> => {
    const response = await apiClient.get<{ crew_member?: unknown; data?: unknown }>(
      `/crew/${encodeURIComponent(crewId)}`
    );
    const rawData = response.data.crew_member || response.data.data || response.data;
    return toCamelCase(rawData as Record<string, unknown>) as unknown as CrewMember;
  },
};
